import {type List, Map} from 'immutable';

/**
 * Minimal shape of the question_set models returned by `getQuestionSets()`,
 * covering only the accessors used here. The model layer is dynamic JSON:API,
 * so values that aren't statically knowable are typed as `unknown`.
 */
interface TemplateQuestionSet {
  getQuestions(): List<unknown>;
  getTemplateRelationships(id: string): {getPosition(): number};
}

/**
 * Describes the `this` context the extension methods run in once mixed into
 * the template model (see `resources/modelRegistry`). It covers the
 * generated/base model accessors used here plus the sibling extension methods
 * that are called on `this`.
 */
interface TemplateModelContext {
  getId(): string;
  cache<T>(key: string, fn: () => T): T;
  getQuestionSets(): List<TemplateQuestionSet>;
  getOrderedQuestionSets(): List<TemplateQuestionSet>;
  getFirstSetAndQuestion(): Map<string, unknown> | null;
  getTotalQuestions(): number;
}

export const templateModelExtensions = {
  getFirstSetAndQuestion(): Map<string, unknown> | null {
    const set = this.getOrderedQuestionSets().first();
    return Map({
      question: set.getQuestions().first(),
      set
    });
  },
  /**
   * Assignment question_sets ordered by position (cached).
   */
  getOrderedQuestionSets(): List<TemplateQuestionSet> {
    return this.cache('ordered_question_sets', () => {
      // Immutable 3.8 `sortBy` is typed as returning `Iterable<K, V>`; at runtime it
      // preserves the `List`, so narrow back to the declared return type.
      return this.getQuestionSets().sortBy((set?: TemplateQuestionSet) => {
        return set ? set.getTemplateRelationships(this.getId()).getPosition() : 0;
      }) as List<TemplateQuestionSet>;
    });
  },

  /**
   * @todo this should be replaced with `this.getMeta().getCountOfQuestions()` when available.
   * @see {@link https://albert-io.atlassian.net/browse/ALBERT-7439}
   */
  getTotalQuestions(): number {
    return this.cache('total_questions', () => {
      return this.getQuestionSets().reduce(
        (total?: number, set?: TemplateQuestionSet) =>
          (total ?? 0) + (set ? set.getQuestions().size : 0),
        0
      );
    });
  }
} satisfies Record<string, unknown> & ThisType<TemplateModelContext>;
